import React from 'react'
import { RadioButton } from 'primereact/radiobutton'
import { Message } from 'primereact/message'
import { StaticGrid } from 'components/common'
import { ContactTypeIds, SelectableMembershipTypeIds } from 'modules/Constants'
import { isMembershipSelectable, hasOnlySuccessorMemberships } from '../../utils/selectionUtils'
import { useKeyboardNavigation } from '../../hooks/useKeyboardNavigation'

const MembershipsPanel = ({
    memberships,
    selectedMembership,
    onMembershipSelect,
    contactTypeId
}) => {
    const handleKeyDown = useKeyboardNavigation({
        data: memberships,
        selectedItem: selectedMembership,
        keyField: 'lineNum',
        onSelect: onMembershipSelect,
        isSelectable: isMembershipSelectable
    })

    const hasContact = memberships !== null
    const hasMemberships = memberships && memberships.length > 0
    const isMemberContactType = contactTypeId === ContactTypeIds.Member
    
    // Non-selectable membership is selected (not in Group A: Ordinary, Reciprocal, or Payee)
    const isNonSelectableSelected = selectedMembership &&
        !SelectableMembershipTypeIds.includes(selectedMembership.membershipTypeId)
    const hasOnlySuccessors = hasOnlySuccessorMemberships(memberships)

    const membershipTypeTemplate = (rowData) => {
        return (
            <span className={`p-tag custom-badge-base custom-badge-orange-${rowData.membershipTypeId || 1}`}>
                {rowData.membershipType}
            </span>
        )
    }

    const radioTemplate = (rowData) => {
        const selectable = isMembershipSelectable(rowData)

        // Don't show radio button for non-selectable memberships
        if (!selectable) return null

        const isSelected = selectedMembership?.lineNum === rowData.lineNum

        return (
            <RadioButton
                inputId={`membership-${rowData.lineNum}`}
                name="membershipSelection"
                value={rowData}
                onChange={() => onMembershipSelect(rowData)}
                checked={isSelected}
            />
        )
    }

    const dateTemplate = (rowData) => rowData.startDate || '-'

    const rowClassName = (rowData) => {
        const selectable = isMembershipSelectable(rowData)
        return selectable ? '' : 'disabled-row'
    }

    const handleRowClick = (e) => {
        const rowData = e.data
        if (isMembershipSelectable(rowData)) {
            onMembershipSelect(rowData)
        }
    }

    const columnDefinitions = [
        { columnType: 'text', title: 'Select', mapsToField: 'lineNum', template: radioTemplate },
        { columnType: 'text', title: 'Membership type', mapsToField: 'membershipType', template: membershipTypeTemplate },
        { columnType: 'text', title: 'Start date', mapsToField: 'startDate', template: dateTemplate }
    ]

    return (
        <div className="contact-search-panel">
            <h5 className="contact-search-panel-title mb-2">Memberships</h5>

            {isNonSelectableSelected && (
                <Message
                    severity="warn"
                    text="This membership type cannot be added as a contributor"
                    className="mb-2 w-100"
                />
            )}

            {isMemberContactType && hasOnlySuccessors && !isNonSelectableSelected && (
                <Message
                    severity="error"
                    text="Member contacts require a selectable membership type (Ordinary/Reciprocal/Payee) to be added as a contributor."
                    className="mb-2 w-100"
                />
            )}

            {isMemberContactType && !hasMemberships && (
                <Message
                    severity="error"
                    text="Member contacts require a membership to be added as a contributor."
                    className="mb-2 w-100"
                />
            )}

            {!isMemberContactType && !hasMemberships && (
                <Message
                    severity="info"
                    text="No membership records found. This contact can still be added as a contributor."
                    className="mb-2 w-100"
                />
            )}

            {hasContact ? (
                hasMemberships ? (
                    <StaticGrid
                        id="memberships"
                        dataKey="lineNum"
                        data={memberships}
                        columnDefinitions={columnDefinitions}
                        showPaginator={false}
                        scrollHeight="auto"
                        emptyMessage="No memberships found"
                        rowClassName={rowClassName}
                        onRowClick={handleRowClick}
                        onKeyDown={handleKeyDown}
                    />
                ) : (
                    !isMemberContactType && (
                        <div className="contact-search-empty-message">
                            No memberships found for this contact
                        </div>
                    )
                )
            ) : (
                <div className="contact-search-empty-message">
                    Select a contact to view memberships
                </div>
            )}
        </div>
    )
}

export default MembershipsPanel
